<template>
  <div class="dev-note-page w-100">
    <basic-subheader title="Release Notes">
      <template v-slot:actions>
        <b-dropdown
          id="dropdown-filter"
          class="dropdown-form-filter mr-2"
          no-caret
          right
        >
          <template #button-content>
            <span class="svg-icon">
              <inline-svg src="/media/svg/icons/Neolex/Basic/filter.svg" />
            </span>
            Bộ lọc
          </template>
          <b-dropdown-form style="min-width: 300px;">
            <b-container class="p-3">
              <b-row>
                <b-col cols="12">
                  <basic-input
                    label="Tìm kiếm"
                    placeholder="Nhập từ khóa..."
                    name="search"
                    :value.sync="filter.search"
                  />
                </b-col>
                <b-col cols="12">
                  <b-form-group label="Tính năng">
                    <b-form-select
                      v-model="filter.feature"
                      :options="featureFilterOptions"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <div class="d-flex justify-content-end mt-3">
                <b-button class="btn btn-secondary mr-2" @click="resetFilter">Reset</b-button>
                <b-button class="btn btn-primary" @click="loadNotes">Lọc</b-button>
              </div>
            </b-container>
          </b-dropdown-form>
        </b-dropdown>
        <b-button
          v-if="isInsertPermission"
          class="btn btn-success ml-2"
          type="button"
          @click="openAddModal"
        >
          <span class="svg-icon">
            <inline-svg src="/media/svg/icons/Neolex/Basic/plus.svg" />
          </span>
          Thêm mới
        </b-button>
      </template>
    </basic-subheader>
    <Wrapper>
      <b-overlay :show="loading">
        <b-tabs content-class="mt-3" v-model="activeTab" @input="onTabChange">
          <b-tab
            v-for="category in categories"
            :key="category.id"
            :title="category.name"
          >
            <div class="release-notes-container">
              <div v-if="getGroupedNotes(category.id).length === 0" class="text-center py-5">
                <p class="text-muted">Chưa có release note nào cho {{ category.name }}</p>
              </div>

              <!-- Tree View grouped by Feature -->
              <div v-else class="feature-tree">
                <div
                  v-for="group in getGroupedNotes(category.id)"
                  :key="group.feature"
                  class="feature-group"
                >
                  <div
                    class="feature-header"
                    @click="toggleFeatureGroup(group.feature)"
                  >
                    <span class="toggle-icon">
                      <inline-svg
                        :src="expandedFeatures.includes(group.feature)
                          ? '/media/svg/icons/Navigation/Angle-down.svg'
                          : '/media/svg/icons/Navigation/Angle-right.svg'"
                        class="svg-icon svg-icon-md"
                      />
                    </span>
                    <span class="feature-name">{{ group.feature || 'Chưa phân loại' }}</span>
                    <b-badge variant="secondary" class="ml-2">{{ group.notes.length }}</b-badge>
                  </div>

                  <b-collapse :visible="expandedFeatures.includes(group.feature)">
                    <div class="feature-notes">
                      <div
                        v-for="note in group.notes"
                        :key="note.id"
                        class="note-item"
                      >
                        <div class="note-card">
                          <div class="note-header">
                            <div class="d-flex align-items-center">
                              <b-badge :variant="getCategoryVariant(category.id)" class="mr-2 version-badge">
                                {{ note.version || 'v1.0.0' }}
                              </b-badge>
                              <h6 class="mb-0 note-title">{{ note.title }}</h6>
                            </div>
                            <div class="note-actions">
                              <small class="text-muted mr-3">{{ formatDate(note.createDatetime) }}</small>
                              <a
                                v-if="isUpdatePermission"
                                href="javascript:;"
                                class="btn btn-icon btn-light btn-hover-primary btn-sm mr-1"
                                title="Cập nhật"
                                @click="editNote(note)"
                              >
                                <span class="svg-icon svg-icon-md svg-icon-primary">
                                  <inline-svg src="/media/svg/icons/Neolex/Basic/edit-2.svg" />
                                </span>
                              </a>
                              <a
                                v-if="isDeletePermission"
                                href="javascript:;"
                                class="btn btn-icon btn-light btn-hover-danger btn-sm"
                                title="Xóa"
                                @click="deleteNote(note)"
                              >
                                <span class="svg-icon svg-icon-md svg-icon-danger">
                                  <inline-svg src="/media/svg/icons/Neolex/Basic/trash-2.svg" />
                                </span>
                              </a>
                            </div>
                          </div>
                          <div class="note-body">
                            <div class="note-content" v-html="formatContent(note.content)"></div>

                            <!-- Files Section -->
                            <div v-if="note.files && note.files.length > 0" class="files-section mt-3">
                              <h6 class="mb-2">Đính kèm</h6>
                              <div class="files-grid">
                                <div
                                  v-for="file in note.files"
                                  :key="file.id"
                                  class="file-item"
                                >
                                  <div v-if="file.fileType === 'image'" class="file-preview image-preview">
                                    <img :src="file.fileUrl" :alt="file.fileName" @click="openFilePreview(file)" />
                                  </div>
                                  <div v-else-if="file.fileType === 'video'" class="file-preview video-preview">
                                    <video :src="file.fileUrl" controls></video>
                                  </div>
                                  <div v-else class="file-preview document-preview" @click="downloadFile(file)">
                                    <inline-svg
                                      :src="getFileIcon(file.mimeType)"
                                      class="file-icon"
                                    />
                                    <span class="file-name">{{ file.fileName }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="note-footer">
                            <small class="text-muted">
                              <span v-if="note.updateDatetime">Cập nhật: {{ formatDate(note.updateDatetime) }}</span>
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>

        <!-- Pagination -->
        <div class="d-flex justify-content-end mt-3" v-if="pagination.total > pagination.size">
          <b-pagination
            v-model="pagination.page"
            :total-rows="pagination.total"
            :per-page="pagination.size"
            @change="onPageChange"
          />
        </div>
      </b-overlay>

      <!-- Add/Edit Modal -->
      <b-modal
        id="dev-note-modal"
        :title="isEditing ? 'Chỉnh sửa Release Note' : 'Thêm Release Note mới'"
        size="xl"
        ok-title="Lưu"
        ok-variant="success"
        cancel-title="Hủy"
        :ok-disabled="saving"
        @ok="saveNote"
        @hidden="resetForm"
      >
        <b-overlay :show="saving">
          <b-form>
            <b-row>
              <b-col cols="4">
                <b-form-group label="Danh mục *" label-for="category">
                  <b-form-select
                    id="category"
                    v-model="form.category"
                    :options="categoryOptions"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col cols="4">
                <b-form-group label="Tính năng *" label-for="feature">
                  <b-form-select
                    v-if="!useCustomFeature"
                    id="feature"
                    v-model="form.feature"
                    :options="featureOptions"
                  />
                  <b-form-input
                    v-else
                    id="featureCustom"
                    v-model="form.feature"
                    placeholder="Nhập tên tính năng"
                  />
                  <b-form-checkbox
                    v-model="useCustomFeature"
                    class="mt-1"
                    size="sm"
                  >
                    Nhập tính năng khác
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col cols="4">
                <b-form-group label="Phiên bản" label-for="version">
                  <b-form-input
                    id="version"
                    v-model="form.version"
                    placeholder="vd: v1.2.0"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group label="Tiêu đề *" label-for="title">
                  <b-form-input
                    id="title"
                    v-model="form.title"
                    placeholder="Nhập tiêu đề release note"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group label="Nội dung *" label-for="content">
              <b-form-textarea
                id="content"
                v-model="form.content"
                placeholder="Nhập nội dung (hỗ trợ markdown cơ bản: **bold**, *italic*, `code`, - list)"
                rows="10"
                required
              />
            </b-form-group>

            <!-- File Upload Section -->
            <b-form-group label="Đính kèm">
              <div class="file-upload-area">
                <b-form-file
                  v-model="newFiles"
                  multiple
                  placeholder="Chọn file hoặc kéo thả vào đây..."
                  drop-placeholder="Thả file vào đây..."
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  @input="onFilesSelected"
                />
                <small class="text-muted mt-1 d-block">
                  Hỗ trợ: Hình ảnh (PNG, JPG, GIF), Video (MP4, WebM), Office (PDF, Word, Excel, PowerPoint)
                </small>
              </div>

              <div v-if="newFiles && newFiles.length > 0" class="mt-3">
                <h6>File mới:</h6>
                <div class="uploaded-files">
                  <div
                    v-for="(file, index) in newFiles"
                    :key="'new-' + index"
                    class="uploaded-file-item"
                  >
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size">{{ formatFileSize(file.size) }}</span>
                    <a
                      href="javascript:;"
                      class="btn btn-icon btn-light btn-hover-danger btn-sm"
                      title="Xóa"
                      @click="removeNewFile(index)"
                    >
                      <span class="svg-icon svg-icon-md svg-icon-danger">
                        <inline-svg src="/media/svg/icons/Neolex/Basic/trash-2.svg" />
                      </span>
                    </a>
                  </div>
                </div>
              </div>

              <div v-if="isEditing && existingFiles.length > 0" class="mt-3">
                <h6>File hiện tại:</h6>
                <div class="uploaded-files">
                  <div
                    v-for="file in existingFiles"
                    :key="file.id"
                    class="uploaded-file-item"
                    :class="{ 'to-delete': deleteFileIds.includes(file.id) }"
                  >
                    <span class="file-name">{{ file.fileName }}</span>
                    <span class="file-size">{{ formatFileSize(file.fileSize) }}</span>
                    <a
                      v-if="!deleteFileIds.includes(file.id)"
                      href="javascript:;"
                      class="btn btn-icon btn-light btn-hover-danger btn-sm"
                      title="Xóa"
                      @click="markFileForDeletion(file.id)"
                    >
                      <span class="svg-icon svg-icon-md svg-icon-danger">
                        <inline-svg src="/media/svg/icons/Neolex/Basic/trash-2.svg" />
                      </span>
                    </a>
                    <a
                      v-else
                      href="javascript:;"
                      class="btn btn-icon btn-light btn-hover-success btn-sm"
                      title="Hoàn tác"
                      @click="unmarkFileForDeletion(file.id)"
                    >
                      <span class="svg-icon svg-icon-md svg-icon-success">
                        <inline-svg src="/media/svg/icons/Neolex/Basic/undo.svg" />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </b-form-group>
          </b-form>
        </b-overlay>
      </b-modal>

      <!-- Delete Confirmation Modal -->
      <b-modal
        id="delete-confirm-modal"
        title="Xác nhận xóa"
        @ok="confirmDelete"
        ok-variant="danger"
        ok-title="Xóa"
        cancel-title="Hủy"
      >
        <p>Bạn có chắc chắn muốn xóa release note "<strong>{{ noteToDelete?.title }}</strong>" không?</p>
      </b-modal>

      <!-- File Preview Modal -->
      <b-modal
        id="file-preview-modal"
        :title="previewFile?.fileName"
        size="xl"
        hide-footer
      >
        <div class="text-center">
          <img
            v-if="previewFile?.fileType === 'image'"
            :src="previewFile?.fileUrl"
            class="img-fluid"
          />
        </div>
      </b-modal>
    </Wrapper>
  </div>
</template>

<script>
export default {
  name: 'DevNote',
  data() {
    return {
      loading: false,
      saving: false,
      activeTab: 0,
      categories: [
        { id: 'Portal', name: 'Portal', variant: 'primary' },
        { id: 'API', name: 'API', variant: 'success' },
        { id: 'Mobile', name: 'Mobile', variant: 'warning' },
      ],
      // Danh sách tính năng từ menu
      features: [
        'Dashboard',
        'Quản lý tài khoản',
        'Quản lý khách hàng',
        'Quản lý gói dịch vụ',
        'Lịch hẹn',
        'Báo cáo',
        'Cài đặt',
        'Bài học',
        'Khảo sát',
        'Thông báo',
        'Voucher',
        'Khác',
      ],
      notes: [],
      pagination: {
        page: 1,
        size: 100,
        total: 0,
      },
      filter: {
        search: '',
        feature: null,
      },
      form: {
        id: null,
        category: 'Portal',
        feature: '',
        title: '',
        content: '',
        version: '',
      },
      useCustomFeature: false,
      newFiles: [],
      existingFiles: [],
      deleteFileIds: [],
      isEditing: false,
      noteToDelete: null,
      previewFile: null,
      expandedFeatures: [],
    };
  },
  computed: {
    categoryOptions() {
      return this.categories.map(cat => ({
        value: cat.id,
        text: cat.name,
      }));
    },
    featureOptions() {
      return [
        { value: '', text: '-- Chọn tính năng --' },
        ...this.features.map(f => ({ value: f, text: f })),
      ];
    },
    featureFilterOptions() {
      return [
        { value: null, text: 'Tất cả tính năng' },
        ...this.features.map(f => ({ value: f, text: f })),
      ];
    },
    currentCategory() {
      return this.categories[this.activeTab]?.id || 'Portal';
    },
  },
  mounted() {
    this.loadNotes();
  },
  methods: {
    async loadNotes() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          category: this.currentCategory,
        };
        if (this.filter.search) {
          params.search = this.filter.search;
        }
        if (this.filter.feature) {
          params.feature = this.filter.feature;
        }

        const { data } = await this.$api.get('DevNote', { params });
        this.notes = data.items || [];
        this.pagination.total = data.total || 0;

        // Expand all features by default
        const uniqueFeatures = [...new Set(this.notes.map(n => n.feature || ''))];
        this.expandedFeatures = uniqueFeatures;
      } catch (error) {
        console.error('Error loading notes:', error);
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không thể tải danh sách release notes',
        });
      } finally {
        this.loading = false;
      }
    },

    getGroupedNotes(categoryId) {
      const categoryNotes = this.notes.filter(note => note.category === categoryId);

      // Group by feature
      const groups = {};
      categoryNotes.forEach(note => {
        const feature = note.feature || '';
        if (!groups[feature]) {
          groups[feature] = [];
        }
        groups[feature].push(note);
      });

      // Convert to array and sort notes within each group by createDatetime desc
      return Object.keys(groups)
        .sort()
        .map(feature => ({
          feature,
          notes: groups[feature].sort((a, b) =>
            new Date(b.createDatetime) - new Date(a.createDatetime)
          ),
        }));
    },

    toggleFeatureGroup(feature) {
      const index = this.expandedFeatures.indexOf(feature);
      if (index > -1) {
        this.expandedFeatures.splice(index, 1);
      } else {
        this.expandedFeatures.push(feature);
      }
    },

    getCategoryVariant(categoryId) {
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.variant : 'secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '';

      let date;

      // Handle different date formats
      if (typeof dateString === 'number') {
        // Unix timestamp - check if seconds or milliseconds
        date = dateString > 9999999999
          ? new Date(dateString)
          : new Date(dateString * 1000);
      } else if (typeof dateString === 'string') {
        // Try parsing as ISO string or .NET date format
        // .NET sometimes returns "/Date(timestamp)/" format
        const dotNetMatch = dateString.match(/\/Date\((\d+)\)\//);
        if (dotNetMatch) {
          date = new Date(parseInt(dotNetMatch[1]));
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }

      // Check if date is valid and not epoch
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return '';
      }

      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatContent(content) {
      if (!content) return '';
      return content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/^- (.*)$/gm, '<li>$1</li>');
    },

    formatFileSize(bytes) {
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      let unitIndex = 0;
      let size = bytes;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`;
    },

    getFileIcon(mimeType) {
      if (!mimeType) return '/media/svg/icons/Files/File.svg';
      if (mimeType.includes('pdf')) return '/media/svg/icons/Files/Pdf.svg';
      if (mimeType.includes('word') || mimeType.includes('document')) return '/media/svg/icons/Files/Word.svg';
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '/media/svg/icons/Files/Excel.svg';
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '/media/svg/icons/Files/Powerpoint.svg';
      return '/media/svg/icons/Files/File.svg';
    },

    onTabChange() {
      this.pagination.page = 1;
      this.loadNotes();
    },

    onPageChange(page) {
      this.pagination.page = page;
      this.loadNotes();
    },

    resetFilter() {
      this.filter = { search: '', feature: null };
      this.pagination.page = 1;
      this.loadNotes();
    },

    openAddModal() {
      this.isEditing = false;
      this.useCustomFeature = false;
      this.form = {
        id: null,
        category: this.currentCategory,
        feature: '',
        title: '',
        content: '',
        version: '',
      };
      this.newFiles = [];
      this.existingFiles = [];
      this.deleteFileIds = [];
      this.$bvModal.show('dev-note-modal');
    },

    editNote(note) {
      this.isEditing = true;
      // Check if feature is in predefined list
      this.useCustomFeature = !this.features.includes(note.feature);
      this.form = {
        id: note.id,
        category: note.category,
        feature: note.feature || '',
        title: note.title,
        content: note.content,
        version: note.version || '',
      };
      this.newFiles = [];
      this.existingFiles = note.files || [];
      this.deleteFileIds = [];
      this.$bvModal.show('dev-note-modal');
    },

    onFilesSelected() {
      // Files are already in newFiles via v-model
    },

    removeNewFile(index) {
      this.newFiles.splice(index, 1);
    },

    markFileForDeletion(fileId) {
      this.deleteFileIds.push(fileId);
    },

    unmarkFileForDeletion(fileId) {
      this.deleteFileIds = this.deleteFileIds.filter(id => id !== fileId);
    },

    async saveNote(bvModalEvt) {
      bvModalEvt.preventDefault();

      if (!this.form.title || !this.form.content) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Vui lòng nhập tiêu đề và nội dung',
        });
        return;
      }

      this.saving = true;
      try {
        const formData = new FormData();
        formData.append('title', this.form.title);
        formData.append('content', this.form.content);
        formData.append('category', this.form.category);
        formData.append('feature', this.form.feature || '');
        formData.append('version', this.form.version || '');

        if (this.newFiles && this.newFiles.length > 0) {
          this.newFiles.forEach(file => {
            formData.append('files', file);
          });
        }

        if (this.isEditing) {
          formData.append('id', this.form.id);
          this.deleteFileIds.forEach(id => {
            formData.append('deleteFileIds', id);
          });
          await this.$api.put('DevNote', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          await this.$api.post('DevNote', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }

        this.$bvModal.hide('dev-note-modal');
        this.$toastr.s({
          title: 'Thành công',
          msg: this.isEditing ? 'Đã cập nhật release note' : 'Đã thêm release note mới',
        });
        this.loadNotes();
      } catch (error) {
        console.error('Error saving note:', error);
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không thể lưu release note',
        });
      } finally {
        this.saving = false;
      }
    },

    deleteNote(note) {
      this.noteToDelete = note;
      this.$bvModal.show('delete-confirm-modal');
    },

    async confirmDelete() {
      if (!this.noteToDelete) return;

      try {
        await this.$api.delete(`DevNote/${this.noteToDelete.id}`);
        this.$toastr.s({
          title: 'Thành công',
          msg: 'Đã xóa release note',
        });
        this.noteToDelete = null;
        this.loadNotes();
      } catch (error) {
        console.error('Error deleting note:', error);
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không thể xóa release note',
        });
      }
    },

    resetForm() {
      this.form = {
        id: null,
        category: 'Portal',
        feature: '',
        title: '',
        content: '',
        version: '',
      };
      this.useCustomFeature = false;
      this.newFiles = [];
      this.existingFiles = [];
      this.deleteFileIds = [];
      this.isEditing = false;
    },

    openFilePreview(file) {
      this.previewFile = file;
      this.$bvModal.show('file-preview-modal');
    },

    downloadFile(file) {
      window.open(file.fileUrl, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.dev-note-page {
  .release-notes-container {
    max-width: 1000px;
    margin: 0 auto;
  }

  .feature-tree {
    .feature-group {
      margin-bottom: 10px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      overflow: hidden;
    }

    .feature-header {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      background: #f8f9fa;
      cursor: pointer;
      transition: background 0.2s ease;

      &:hover {
        background: #e9ecef;
      }

      .toggle-icon {
        margin-right: 8px;
      }

      .feature-name {
        font-weight: 600;
        font-size: 1rem;
      }
    }

    .feature-notes {
      padding: 10px;
      background: #fff;
    }
  }

  .note-item {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .note-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  }

  .note-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #fafbfc;
    border-bottom: 1px solid #e9ecef;
  }

  .note-title {
    font-size: 0.95rem;
    font-weight: 500;
  }

  .version-badge {
    font-size: 0.75rem;
    font-weight: 600;
  }

  .note-body {
    padding: 16px;
  }

  .note-footer {
    padding: 8px 16px;
    background: #fafbfc;
    border-top: 1px solid #e9ecef;
  }

  .note-content {
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.6;
    font-size: 0.9rem;

    code {
      background-color: #f4f4f4;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 0.85em;
    }

    li {
      list-style-type: disc;
      margin-left: 20px;
    }
  }

  .files-section {
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
  }

  .files-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
  }

  .file-item {
    .file-preview {
      border-radius: 6px;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.02);
      }
    }

    .image-preview img {
      width: 100%;
      height: 80px;
      object-fit: cover;
    }

    .video-preview video {
      width: 100%;
      height: 80px;
      object-fit: cover;
    }

    .document-preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 12px;
      background: #f8f9fa;
      border: 1px solid #e9ecef;

      .file-icon {
        width: 32px;
        height: 32px;
        margin-bottom: 6px;
      }

      .file-name {
        font-size: 0.7rem;
        text-align: center;
        word-break: break-all;
      }
    }
  }

  .file-upload-area {
    padding: 16px;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
  }

  .uploaded-files {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .uploaded-file-item {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    background: #f8f9fa;
    border-radius: 4px;

    &.to-delete {
      opacity: 0.5;
      text-decoration: line-through;
    }

    .file-name {
      flex: 1;
      margin-right: 8px;
      word-break: break-all;
      font-size: 0.85rem;
    }

    .file-size {
      color: #6c757d;
      font-size: 0.8rem;
      margin-right: 8px;
    }
  }

}
</style>
